/**
 * GUFUMART PDP delivery estimates.
 * Calculation is separate from rendering. PDP shipping is a supplier-band
 * estimate and is not the Shopify checkout shipping rate.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
  if (root) {
    root.GfPdpDelivery = api;
    if (typeof document !== 'undefined') {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
          api.bindAll(document);
        });
      } else {
        api.bindAll(document);
      }
    }
  }
})(typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : this, function () {
  var EMPTY = '—';
  var GRAMS_PER_POUND = 453.59237;
  var ZIP_STORAGE_KEY = 'gufumart_delivery_zip';
  var ZIPPOPOTAM_ORIGIN = 'https://api.zippopotam.us';
  var LOOKUP_TIMEOUT_MS = 6000;
  var POSTAL_PATH_PATTERN = /^[A-Z0-9][A-Z0-9 \-]{0,14}$/;
  var PANEL_TRANSITION_MS = 180;

  /**
   * Central supplier rules for this first PDP estimate.
   * Shipping bands are current US Dropship & Sample rates, not wholesale
   * and not live Shopify checkout rates.
   * Returns: Trendsi return service is for US orders only. If a product
   * cannot be confidently classified as non-returnable, treat it as eligible.
   */
  var CONFIG = {
    zipPattern: /^(\d{5})(?:-?(\d{4}))?$/,
    origin: {
      usa: { minBusinessDays: 2, maxBusinessDays: 5 },
      overseas: { minBusinessDays: 5, maxBusinessDays: 10 },
    },
    shipping: {
      source: 'usa_dropship_sample',
      currency: 'USD',
      bands: [
        { maxLb: 0.6, amount: 3.99 },
        { maxLb: 1, amount: 5.99 },
        { maxLb: 5, amount: 9.99 },
        { maxLb: Infinity, amount: 14.99 },
      ],
    },
    returns: {
      eligibleLabel: '7 Days',
      finalSaleLabel: 'Final Sale',
      notAvailableLabel: 'Not Available',
      nonReturnable: [
        {
          id: 'beauty',
          needles: [
            'beauty',
            'makeup',
            'skincare',
            'skin care',
            'hair care',
            'haircare',
            'body care',
            'beauty tools',
            'grooming',
            'personal care',
          ],
        },
        { id: 'bodysuits', needles: ['bodysuit', 'bodysuits', 'body suit'] },
        { id: 'face_masks', needles: ['face mask', 'face masks', 'facemask'] },
        { id: 'intimates', needles: ['intimate', 'intimates', 'lingerie', 'underwear'] },
        { id: 'jewelry', needles: ['jewelry', 'jewellery'] },
        { id: 'swimwear', needles: ['swimwear', 'swimsuit', 'swim wear', 'bikini'] },
        { id: 'openpack_wholesale', needles: ['openpack', 'open pack', 'wholesale'] },
      ],
    },
    copy: {
      intro: 'Enter a ZIP code to check delivery availability. Exact date and cost are confirmed at checkout.',
      invalid: 'Enter a valid postal code.',
      lookupFailed: 'Enter a valid postal code.',
      unavailable: 'Unavailable',
      ready: 'Estimates are for planning only. Exact date and cost are confirmed at checkout.',
      originMissing: 'Delivery estimate unavailable',
    },
    cj: {
      etaText: '5–8 Business Days',
      shippingText: 'FREE SHIPPING',
      returnText: '7 Days',
      returnCode: 'eligible',
    },
    postal: {
      default: {
        label: 'Postal Code',
        placeholder: 'Enter postal code',
        inputmode: 'text',
        maxlength: 12,
        pattern: /^[A-Z0-9][A-Z0-9 \-]{1,10}$/i,
      },
    },
  };

  var NUMERIC_INPUT_COUNTRIES = {
    US: true,
    IN: true,
    AU: true,
    DE: true,
    FR: true,
    IT: true,
    ES: true,
    NZ: true,
  };

  var POSTAL_RULES = {
    US: {
      label: 'ZIP Code',
      placeholder: 'Enter ZIP code',
      inputmode: 'numeric',
      maxlength: 10,
      pattern: /^(\d{5})(?:-?(\d{4}))?$/,
      format: function (match) {
        return match[2] ? match[1] + '-' + match[2] : match[1];
      },
    },
    CA: {
      label: 'Postal Code',
      placeholder: 'Enter postal code',
      inputmode: 'text',
      maxlength: 7,
      pattern: /^([ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z])(?:[ -]?(\d[ABCEGHJ-NPRSTV-Z]\d))?$/i,
      format: function (match) {
        var fsa = String(match[1] || '').toUpperCase();
        var ldu = String(match[2] || '').toUpperCase();
        return ldu ? fsa + ' ' + ldu : fsa;
      },
    },
    GB: {
      label: 'Postcode',
      placeholder: 'Enter Postcode',
      inputmode: 'text',
      maxlength: 8,
      pattern: /^([A-Z]{1,2}\d[A-Z\d]?)(?:\s*(\d[A-Z]{2}))?$/i,
      format: function (match) {
        var outward = String(match[1] || '').toUpperCase();
        var inward = String(match[2] || '').toUpperCase();
        return inward ? outward + ' ' + inward : outward;
      },
    },
    AU: { label: 'Postcode', placeholder: 'Enter postcode', inputmode: 'numeric', maxlength: 4, pattern: /^\d{4}$/ },
    NZ: { label: 'Postcode', placeholder: 'Enter postcode', inputmode: 'numeric', maxlength: 4, pattern: /^\d{4}$/ },
    DE: { label: 'PLZ', placeholder: 'Enter PLZ', inputmode: 'numeric', maxlength: 5, pattern: /^\d{5}$/ },
    FR: { label: 'Postal Code', placeholder: 'Enter Postal Code', inputmode: 'numeric', maxlength: 5, pattern: /^\d{5}$/ },
    IT: { label: 'CAP', placeholder: 'Enter CAP', inputmode: 'numeric', maxlength: 5, pattern: /^\d{5}$/ },
    ES: { label: 'Código Postal', placeholder: 'Enter código postal', inputmode: 'numeric', maxlength: 5, pattern: /^\d{5}$/ },
    JP: {
      label: 'Postal Code',
      placeholder: 'Enter postal code',
      inputmode: 'numeric',
      maxlength: 8,
      pattern: /^(\d{3})-?(\d{4})$/,
      format: function (match) {
        return match[1] + '-' + match[2];
      },
    },
    NL: {
      label: 'Postcode',
      placeholder: 'Enter postcode',
      inputmode: 'text',
      maxlength: 7,
      pattern: /^(\d{4})\s*([A-Z]{2})$/i,
      format: function (match) {
        return match[1] + ' ' + match[2].toUpperCase();
      },
    },
    IE: {
      label: 'Eircode',
      placeholder: 'Enter Eircode',
      inputmode: 'text',
      maxlength: 8,
      pattern: /^([AC-FHKNPRTV-Y]\d{2})\s*([AC-FHKNPRTV-Y0-9]{4})$/i,
      format: function (match) {
        return (match[1] + ' ' + match[2]).toUpperCase();
      },
    },
    AT: { label: 'PLZ', placeholder: 'Enter PLZ', inputmode: 'numeric', maxlength: 4, pattern: /^\d{4}$/ },
    BE: { label: 'Postal Code', placeholder: 'Enter postal code', inputmode: 'numeric', maxlength: 4, pattern: /^\d{4}$/ },
    CH: { label: 'PLZ', placeholder: 'Enter PLZ', inputmode: 'numeric', maxlength: 4, pattern: /^\d{4}$/ },
    DK: { label: 'Postal Code', placeholder: 'Enter postal code', inputmode: 'numeric', maxlength: 4, pattern: /^\d{4}$/ },
    FI: { label: 'Postal Code', placeholder: 'Enter postal code', inputmode: 'numeric', maxlength: 5, pattern: /^\d{5}$/ },
    NO: { label: 'Postal Code', placeholder: 'Enter postal code', inputmode: 'numeric', maxlength: 4, pattern: /^\d{4}$/ },
    SE: { label: 'Postal Code', placeholder: 'Enter postal code', inputmode: 'numeric', maxlength: 6, pattern: /^\d{3}\s?\d{2}$/ },
    PL: { label: 'Postal Code', placeholder: 'Enter postal code', inputmode: 'text', maxlength: 6, pattern: /^\d{2}-?\d{3}$/ },
    PT: { label: 'Postal Code', placeholder: 'Enter postal code', inputmode: 'text', maxlength: 8, pattern: /^\d{4}-?\d{3}$/ },
    SG: { label: 'Postal Code', placeholder: 'Enter postal code', inputmode: 'numeric', maxlength: 6, pattern: /^\d{6}$/ },
    KR: { label: 'Postal Code', placeholder: 'Enter postal code', inputmode: 'numeric', maxlength: 5, pattern: /^\d{5}$/ },
    IN: { label: 'Postal Code', placeholder: 'Enter postal code', inputmode: 'numeric', maxlength: 6, pattern: /^\d{6}$/ },
    AE: { label: 'Postal Code', placeholder: 'Enter postal code', inputmode: 'text', maxlength: 10, pattern: /^[A-Z0-9][A-Z0-9 \-]{1,8}$/i },
    HK: { label: 'Postal Code', placeholder: 'Enter postal code', inputmode: 'text', maxlength: 10, pattern: /^[A-Z0-9][A-Z0-9 \-]{1,8}$/i },
  };

  function trim(value) {
    return String(value == null ? '' : value).replace(/^\s+|\s+$/g, '');
  }

  function normalizeKey(value) {
    return trim(value)
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ');
  }

  function destinationCountry(root) {
    return String((root && root.getAttribute('data-gf-country')) || '')
      .trim()
      .toUpperCase();
  }

  function compactPostal(value) {
    return String(value || '')
      .toUpperCase()
      .replace(/[\s-]+/g, '');
  }

  function uniqueValues(values) {
    var seen = {};
    var out = [];
    (values || []).forEach(function (value) {
      if (!value || seen[value]) return;
      seen[value] = true;
      out.push(value);
    });
    return out;
  }

  function zippopotamCandidates(postal) {
    var trimmed = trim(postal);
    var compact = compactPostal(trimmed);
    var candidates = [trimmed, compact];
    if (/^\d{7}$/.test(compact)) candidates.push(compact.slice(0, 3) + '-' + compact.slice(3));
    if (/^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(compact)) candidates.push(compact.slice(0, 3));
    if (/^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/.test(compact) && compact.length >= 5) {
      candidates.push(compact.slice(0, compact.length - 3));
    }
    return uniqueValues(candidates);
  }

  function zippopotamUrl(country, postal) {
    var iso = String(country || '').trim().toUpperCase();
    var pathPostal = trim(postal).toUpperCase();
    if (!/^[A-Z]{2}$/.test(iso)) return '';
    if (!POSTAL_PATH_PATTERN.test(pathPostal)) return '';
    return ZIPPOPOTAM_ORIGIN + '/' + encodeURIComponent(iso) + '/' + encodeURIComponent(pathPostal);
  }

  function placeFromZippopotam(payload, country, postal) {
    if (!payload || typeof payload !== 'object') return { valid: false };
    var resultCountry = String(payload['country abbreviation'] || '').trim().toUpperCase();
    if (!resultCountry || resultCountry !== String(country || '').trim().toUpperCase()) {
      return { valid: false };
    }
    var places = Array.isArray(payload.places) ? payload.places : [];
    var match = null;
    places.forEach(function (item) {
      if (match || !item) return;
      var name = trim(item['place name']);
      var state = trim(item.state);
      if (name || state) match = item;
    });
    if (!match) return { valid: false };
    var placeName = trim(match['place name']);
    var region = trim(match.state);
    var regionCode = trim(match['state abbreviation']);
    if (!placeName && !region) return { valid: false };
    return {
      valid: true,
      country: resultCountry,
      postal: trim(payload['post code']) || trim(postal),
      place: placeName,
      region: region,
      region_code: regionCode,
    };
  }

  function fetchWithTimeout(url) {
    var options = { method: 'GET', headers: { Accept: 'application/json' } };
    if (typeof AbortController === 'undefined') return fetch(url, options);
    var controller = new AbortController();
    var timer = setTimeout(function () {
      controller.abort();
    }, LOOKUP_TIMEOUT_MS);
    options.signal = controller.signal;
    return fetch(url, options).then(
      function (response) {
        clearTimeout(timer);
        return response;
      },
      function (err) {
        clearTimeout(timer);
        throw err;
      }
    );
  }

  function postalRule(country) {
    var iso = String(country || '').trim().toUpperCase();
    return POSTAL_RULES[iso] || CONFIG.postal.default;
  }

  function postalInputMode(country) {
    var iso = String(country || '').trim().toUpperCase();
    return NUMERIC_INPUT_COUNTRIES[iso] ? 'numeric' : 'text';
  }

  function deliveryHintText(country) {
    var iso = String(country || '').trim().toUpperCase();
    if (iso === 'US') return 'Enter ZIP Code to Check Delivery Availability';
    if (iso === 'IN') return 'Enter PIN Code to Check Delivery Availability';
    if (iso === 'GB' || iso === 'AU') return 'Enter Postcode to Check Delivery Availability';
    return 'Enter Postal Code to Check Delivery Availability';
  }

  function normalizePostal(country, value) {
    var iso = String(country || '').trim().toUpperCase();
    var rule = postalRule(iso);
    var raw = trim(value);
    if (!raw) return { ok: false, value: raw, country: iso };
    var match = raw.match(rule.pattern);
    if (!match) return { ok: false, value: raw, country: iso };
    var formatted = typeof rule.format === 'function' ? rule.format(match) : raw.toUpperCase();
    return { ok: true, value: formatted, country: iso };
  }

  function normalizeZip(value) {
    return normalizePostal('US', value);
  }

  function invalidPostalMessage(country) {
    var label = postalRule(country).label || 'postal code';
    return 'Enter a valid ' + label + '.';
  }

  function getProductOrigin(tags) {
    var list = Array.isArray(tags)
      ? tags
      : String(tags || '')
          .split('|')
          .map(trim)
          .filter(Boolean);
    var usa = false;
    var overseas = false;
    list.forEach(function (tag) {
      var key = normalizeKey(tag);
      if (key === 'ship from usa') usa = true;
      if (key === 'ship from overseas') overseas = true;
    });
    if (usa && !overseas) return 'usa';
    if (overseas && !usa) return 'overseas';
    return null;
  }

  function variantSku(input) {
    if (!input) return '';
    if (typeof input === 'string') return trim(input);
    return trim(
      input.sku ||
        input.SKU ||
        (input.variant && (input.variant.sku || input.variant.SKU)) ||
        ''
    );
  }

  function isCjSku(value) {
    return variantSku(value).toUpperCase().indexOf('CJ') === 0;
  }

  function skuFromPage(root) {
    var fromVariant = variantSku(getSelectedVariant(root));
    if (fromVariant) return fromVariant;
    var scope =
      (root && root.closest && (root.closest('product-info') || root)) ||
      (typeof document !== 'undefined' ? document : null);
    if (scope && scope.querySelector) {
      var selected = scope.querySelector('[data-selected-variant]');
      var parsed = selected && parseJson(selected.textContent);
      if (parsed && parsed.sku) return trim(parsed.sku);
    }
    if (typeof document !== 'undefined') {
      var nodes = document.querySelectorAll('script[type="application/ld+json"]');
      for (var i = 0; i < nodes.length; i += 1) {
        var data = parseJson(nodes[i].textContent);
        if (!data) continue;
        if (data.sku) return trim(data.sku);
        var variants = data.hasVariant;
        if (Array.isArray(variants)) {
          for (var j = 0; j < variants.length; j += 1) {
            if (variants[j] && variants[j].sku) return trim(variants[j].sku);
          }
        }
      }
    }
    try {
      var analytics =
        typeof window !== 'undefined' &&
        window.ShopifyAnalytics &&
        window.ShopifyAnalytics.meta &&
        window.ShopifyAnalytics.meta.product;
      var list = analytics && analytics.variants;
      if (list && list[0] && list[0].sku) return trim(list[0].sku);
    } catch (err) {}
    return '';
  }

  function isCjSupplier(input) {
    var fulfillKey = normalizeKey(
      input && (input.fulfillmentService || input.fulfillment_service)
    );
    if (fulfillKey === 'cjdropshipping' || fulfillKey === 'cj dropshipping') return true;
    return isCjSku(input);
  }

  function parseJson(text) {
    try {
      return JSON.parse(text);
    } catch (err) {
      return null;
    }
  }

  function getSelectedVariant(root) {
    if (!root) return null;
    var script = root.querySelector('[data-gf-pdp-variant]');
    var fromScript = script && script.textContent ? parseJson(script.textContent) : null;
    var productInfo = (root.closest && root.closest('product-info')) || root || document;
    var selected =
      productInfo && productInfo.querySelector
        ? productInfo.querySelector('variant-selects [data-selected-variant], [data-selected-variant]')
        : null;
    var fromSelected = selected && selected.textContent ? parseJson(selected.textContent) : null;
    if (fromScript && fromScript.id) {
      if (!fromScript.sku && fromSelected && fromSelected.sku) fromScript.sku = fromSelected.sku;
      return fromScript;
    }
    return fromSelected || fromScript;
  }

  function normalizeWeightUnit(unit) {
    var key = normalizeKey(unit);
    if (key === 'lb' || key === 'lbs' || key === 'pound' || key === 'pounds') return 'lb';
    if (key === 'oz' || key === 'ounce' || key === 'ounces') return 'oz';
    if (key === 'kg' || key === 'kilogram' || key === 'kilograms') return 'kg';
    if (key === 'g' || key === 'gram' || key === 'grams') return 'g';
    return '';
  }

  function convertToPounds(amount, unit) {
    var value = Number(amount);
    if (!isFinite(value) || value < 0) return 0;
    if (unit === 'lb') return value;
    if (unit === 'oz') return value / 16;
    if (unit === 'kg') return value * 2.2046226218;
    if (unit === 'g') return value / GRAMS_PER_POUND;
    return value / GRAMS_PER_POUND;
  }

  function getWeightInPounds(variant) {
    if (!variant || typeof variant !== 'object') return 0;

    var grams = Number(variant.grams);
    if (variant.grams != null && variant.grams !== '' && isFinite(grams) && grams >= 0) {
      return grams / GRAMS_PER_POUND;
    }

    var ownUnit = normalizeWeightUnit(variant.weight_unit || variant.weightUnit);
    var inUnit = Number(variant.weight_in_unit);
    if (
      ownUnit &&
      variant.weight_in_unit != null &&
      variant.weight_in_unit !== '' &&
      isFinite(inUnit)
    ) {
      return convertToPounds(inUnit, ownUnit);
    }

    var weight = Number(variant.weight);
    if (!isFinite(weight) || weight < 0) return 0;

    // Shopify Ajax/Liquid `weight` is grams. `weight_unit` is the shop unit
    // and must not be applied to `weight` unless `weight_in_unit` is present.
    if (ownUnit === 'g' || ownUnit === 'kg') return convertToPounds(weight, ownUnit);
    if ((ownUnit === 'lb' && weight > 30) || (ownUnit === 'oz' && weight > 500)) {
      return convertToPounds(weight, 'g');
    }
    if (ownUnit === 'lb' || ownUnit === 'oz') return convertToPounds(weight, ownUnit);
    return convertToPounds(weight, 'g');
  }

  function addBusinessDays(startDate, days) {
    var count = Number(days);
    if (!isFinite(count) || count < 1) return new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    var cursor = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    var added = 0;
    while (added < count) {
      cursor.setDate(cursor.getDate() + 1);
      var weekday = cursor.getDay();
      if (weekday !== 0 && weekday !== 6) added += 1;
    }
    return cursor;
  }

  function formatDate(date) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  function formatDateRange(startDate, endDate) {
    return formatDate(startDate) + ' – ' + formatDate(endDate);
  }

  function getShippingRate(pounds, country) {
    var iso = String(country || 'US').trim().toUpperCase();
    var weight = Number(pounds);
    if (!isFinite(weight) || weight < 0) weight = 0;
    if (iso !== 'US') {
      return {
        amount: null,
        currency: CONFIG.shipping.currency,
        source: CONFIG.shipping.source,
        available: false,
      };
    }
    var amount = CONFIG.shipping.bands[CONFIG.shipping.bands.length - 1].amount;
    for (var i = 0; i < CONFIG.shipping.bands.length; i += 1) {
      if (weight <= CONFIG.shipping.bands[i].maxLb) {
        amount = CONFIG.shipping.bands[i].amount;
        break;
      }
    }
    return {
      amount: amount,
      currency: CONFIG.shipping.currency,
      source: CONFIG.shipping.source,
      available: true,
    };
  }

  function formatShippingAmount(amount) {
    return '$' + Number(amount).toFixed(2);
  }

  function haystackForProduct(product) {
    var typeKey = normalizeKey(product && product.type);
    var categoryKey = normalizeKey(product && product.editorCategory);
    return {
      type: typeKey,
      category: categoryKey,
      combined: trim(typeKey + ' ' + categoryKey),
    };
  }

  function matchesNeedle(haystack, needle) {
    var n = normalizeKey(needle);
    if (!n || !haystack) return false;
    if ((' ' + haystack + ' ').indexOf(' ' + n + ' ') !== -1) return true;
    if (n.indexOf(' ') === -1 && haystack.indexOf(n) !== -1 && n.length >= 7) return true;
    return false;
  }

  function isNonReturnableCategory(product) {
    var keys = haystackForProduct(product);
    var haystack = keys.combined;
    if (!haystack) return false;
    return CONFIG.returns.nonReturnable.some(function (rule) {
      return rule.needles.some(function (needle) {
        return matchesNeedle(haystack, needle);
      });
    });
  }

  function getReturnStatus(product, destination) {
    if (destination && destination !== 'us') {
      return { code: 'not_available', label: CONFIG.returns.notAvailableLabel };
    }
    if (isNonReturnableCategory(product)) {
      return { code: 'final_sale', label: CONFIG.returns.finalSaleLabel };
    }
    return { code: 'eligible', label: CONFIG.returns.eligibleLabel };
  }

  function mergeVariant(current, incoming, fallbackUnit) {
    var next = incoming && typeof incoming === 'object' ? incoming : current;
    if (!next) return null;
    return {
      id: next.id,
      sku: next.sku || next.SKU || (current && current.sku) || '',
      weight: next.weight != null ? next.weight : current && current.weight,
      weight_unit: next.weight_unit || next.weightUnit || (current && current.weight_unit) || fallbackUnit,
      weight_in_unit: next.weight_in_unit != null ? next.weight_in_unit : current && current.weight_in_unit,
      grams: next.grams != null ? next.grams : current && current.grams,
      requires_shipping: next.requires_shipping != null ? next.requires_shipping : current && current.requires_shipping,
    };
  }

  function productFromRoot(root) {
    return {
      type: root.getAttribute('data-gf-product-type') || '',
      editorCategory: root.getAttribute('data-gf-editor-category') || '',
      tags: (root.getAttribute('data-gf-product-tags') || '').split('|'),
      vendor: root.getAttribute('data-gf-vendor') || '',
      fulfillmentService: root.getAttribute('data-gf-fulfillment') || '',
      freeShippingOffer: root.getAttribute('data-gf-free-shipping-offer') === 'true',
      weightUnit: root.getAttribute('data-gf-weight-unit') || '',
    };
  }

  function clearPlace(root) {
    var place = root.querySelector('[data-gf-delivery-place]');
    if (!place) return;
    place.textContent = '';
    place.hidden = true;
    place.classList.remove('is-visible');
  }

  function renderPlace(root, lookup) {
    var place = root.querySelector('[data-gf-delivery-place]');
    if (!place) return;
    var text = formatPlace(lookup);
    if (!text) {
      clearPlace(root);
      return;
    }
    place.textContent = text;
    place.hidden = false;
    place.classList.add('is-visible');
  }

  function formatPlace(lookup) {
    if (!lookup || !lookup.valid) return '';
    var locality = trim(lookup.place || lookup.city || '');
    var regionPart = trim(lookup.region_code || lookup.region || '');
    var detail = '';
    if (locality && regionPart && locality !== regionPart) detail = locality + ', ' + regionPart;
    else detail = locality || regionPart;
    if (!detail) return '';
    return 'Location: ' + detail;
  }

  function formatDeliveryLocation(lookup) {
    if (!lookup || !lookup.valid) return '';
    var place = trim(lookup.place || '');
    var regionPart = trim(lookup.region_code || lookup.region || '');
    if (place && regionPart && place !== regionPart) return place + ', ' + regionPart;
    return place || regionPart;
  }

  function formatDeliveryTitle(lookup) {
    var loc = formatDeliveryLocation(lookup);
    return loc ? 'Delivery To: ' + loc : 'Delivery To:';
  }

  function renderDeliveryTitle(root, lookup) {
    var title = root.querySelector('[data-gf-delivery-title]');
    if (!title) return;
    var place = title.querySelector('[data-gf-delivery-title-place]');
    var loc = formatDeliveryLocation(lookup);
    if (place) {
      place.textContent = loc;
      return;
    }
    title.textContent = formatDeliveryTitle(lookup);
  }

  function formatZipLocation(postal, lookup) {
    var code = trim(postal);
    if (!lookup || !lookup.valid || !code) return '';
    var place = trim(lookup.place || '');
    var regionPart = trim(lookup.region_code || lookup.region || '');
    var loc = place;
    if (place && regionPart && place !== regionPart) loc = place + ', ' + regionPart;
    else loc = place || regionPart;
    if (!loc) return '';
    return code + ' ' + loc;
  }

  function enteredPostal(root) {
    var input = root && root.querySelector('.gf-pdp-zip__input');
    var typed = input ? trim(input.value) : '';
    return typed || trim((root && root.getAttribute('data-gf-checked-zip')) || '');
  }

  function clearZipLocation(root) {
    var line = root.querySelector('[data-gf-zip-location]');
    if (!line) return;
    line.textContent = '';
    line.hidden = true;
    line.classList.remove('is-visible');
  }

  function renderZipLocation(root, lookup) {
    var line = root.querySelector('[data-gf-zip-location]');
    if (!line) return;
    var text = formatZipLocation(enteredPostal(root), lookup);
    if (!text) {
      clearZipLocation(root);
      return;
    }
    line.textContent = text;
    line.hidden = false;
    line.classList.add('is-visible');
  }

  function clearResult(root) {
    var eta = root.querySelector('[data-gf-delivery-eta]');
    var shipping = root.querySelector('[data-gf-delivery-shipping]');
    var ret = root.querySelector('[data-gf-delivery-return]');
    if (eta) eta.textContent = EMPTY;
    if (shipping) shipping.textContent = EMPTY;
    if (ret) ret.textContent = EMPTY;
    if (shipping) shipping.removeAttribute('data-gf-shipping-promo');
    clearPlace(root);
    clearZipLocation(root);
    renderDeliveryTitle(root, null);
  }

  function renderDeliveryResult(root, result, lookup) {
    var eta = root.querySelector('[data-gf-delivery-eta]');
    var shipping = root.querySelector('[data-gf-delivery-shipping]');
    var ret = root.querySelector('[data-gf-delivery-return]');
    if (eta) eta.textContent = result.etaText || EMPTY;
    if (shipping) {
      shipping.textContent = result.shippingText || EMPTY;
      if (result.shippingPromo) shipping.setAttribute('data-gf-shipping-promo', 'true');
      else shipping.removeAttribute('data-gf-shipping-promo');
    }
    if (ret) ret.textContent = result.returnText || EMPTY;
    if (lookup) {
      clearPlace(root);
      clearZipLocation(root);
      renderDeliveryTitle(root, lookup);
    }
  }

  function setNote(root, message, state) {
    var note = root.querySelector('[data-gf-pdp-zip-note]');
    if (!note) return;
    note.textContent = message;
    note.classList.remove('is-ok', 'is-err');
    if (state) note.classList.add(state);
  }

  function calculateEstimate(input) {
    var destination = String((input && input.destinationCountry) || 'US').trim().toUpperCase();
    if (isCjSupplier(input)) {
      var cjNow = input.now || new Date();
      return {
        origin: 'cj',
        destination: destination,
        pounds: getWeightInPounds(input && input.variant),
        rate: {
          amount: 0,
          currency: CONFIG.shipping.currency,
          source: 'cj_worldwide',
          available: true,
        },
        etaText: formatDateRange(addBusinessDays(cjNow, 5), addBusinessDays(cjNow, 8)),
        shippingText: CONFIG.cj.shippingText,
        shippingPromo: true,
        returnText: CONFIG.cj.returnText,
        returnCode: CONFIG.cj.returnCode,
      };
    }
    var origin = getProductOrigin(input.tags);
    var pounds = getWeightInPounds(input.variant);
    var rate = getShippingRate(pounds, destination);
    var returns = getReturnStatus(
      { type: input.productType, editorCategory: input.editorCategory },
      destination === 'US' ? 'us' : destination.toLowerCase()
    );
    var etaText = CONFIG.copy.originMissing;
    var window = origin ? CONFIG.origin[origin] : null;
    if (window) {
      var now = input.now || new Date();
      etaText = formatDateRange(
        addBusinessDays(now, window.minBusinessDays),
        addBusinessDays(now, window.maxBusinessDays)
      );
    }
    var shippingText = 'FREE SHIPPING';
    var shippingPromo = true;
    if (input.variant && input.variant.requires_shipping === false) {
      rate = { amount: 0, currency: CONFIG.shipping.currency, source: CONFIG.shipping.source, available: true };
    }
    return {
      origin: origin,
      destination: destination,
      pounds: pounds,
      rate: rate,
      etaText: etaText,
      shippingText: shippingText,
      shippingPromo: shippingPromo,
      returnText: returns.label,
      returnCode: returns.code,
    };
  }

  function rememberZip(root, zip) {
    root.setAttribute('data-gf-checked-zip', zip);
  }

  function parseStoredDestination(raw) {
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && parsed.country && parsed.postal) {
        return {
          country: String(parsed.country).trim().toUpperCase(),
          postal: String(parsed.postal).trim(),
        };
      }
    } catch (err) {}
    var legacy = normalizePostal('US', raw);
    if (legacy.ok) return { country: 'US', postal: legacy.value };
    return null;
  }

  function readStoredDestination(currentCountry) {
    try {
      var stored = parseStoredDestination(window.localStorage.getItem(ZIP_STORAGE_KEY));
      if (!stored) return null;
      if (stored.country !== String(currentCountry || '').trim().toUpperCase()) {
        window.localStorage.removeItem(ZIP_STORAGE_KEY);
        return null;
      }
      var parsed = normalizePostal(stored.country, stored.postal);
      if (!parsed.ok) {
        window.localStorage.removeItem(ZIP_STORAGE_KEY);
        return null;
      }
      return { country: stored.country, postal: parsed.value };
    } catch (err) {
      return null;
    }
  }

  function writeStoredDestination(country, postal) {
    try {
      window.localStorage.setItem(
        ZIP_STORAGE_KEY,
        JSON.stringify({
          country: String(country || '').trim().toUpperCase(),
          postal: String(postal || '').trim(),
        })
      );
    } catch (err) {}
  }

  function clearStoredDestination() {
    try {
      window.localStorage.removeItem(ZIP_STORAGE_KEY);
    } catch (err) {}
  }

  function fetchZippopotam(country, postal) {
    var url = zippopotamUrl(country, postal);
    if (!url) return Promise.resolve(null);
    return fetchWithTimeout(url)
      .then(function (response) {
        if (!response || !response.ok) return null;
        return response.json();
      })
      .then(function (payload) {
        if (!payload || typeof payload !== 'object') return null;
        return payload;
      })
      .catch(function () {
        return null;
      });
  }

  function lookupPostal(country, postal) {
    var iso = String(country || '').trim().toUpperCase();
    var value = trim(postal);
    if (!iso || !value) return Promise.resolve({ valid: false });
    var chain = Promise.resolve({ valid: false });
    zippopotamCandidates(value).forEach(function (candidate) {
      chain = chain.then(function (current) {
        if (current && current.valid) return current;
        return fetchZippopotam(iso, candidate).then(function (payload) {
          if (!payload) return { valid: false };
          return placeFromZippopotam(payload, iso, value);
        });
      });
    });
    return chain.catch(function () {
      return { valid: false };
    });
  }

  function getPanel(root) {
    return root.querySelector('[data-gf-pdp-delivery-panel]');
  }

  function isPanelOpen(root) {
    var panel = getPanel(root);
    return !!(panel && panel.classList.contains('is-open'));
  }

  function setCheckExpanded(root, open) {
    var check = root.querySelector('.gf-pdp-zip__check');
    if (check) check.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function openPanel(root) {
    var panel = getPanel(root);
    if (!panel) return;
    if (root._gfPanelHideTimer) {
      clearTimeout(root._gfPanelHideTimer);
      root._gfPanelHideTimer = null;
    }
    panel.hidden = false;
    panel.setAttribute('aria-hidden', 'false');
    root.setAttribute('data-gf-delivery-open', 'true');
    setCheckExpanded(root, true);
    if (panel.classList.contains('is-open')) return;
    void panel.offsetWidth;
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(function () {
        panel.classList.add('is-open');
      });
    } else {
      panel.classList.add('is-open');
    }
  }

  function closePanel(root) {
    var panel = getPanel(root);
    if (!panel) return;
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    root.removeAttribute('data-gf-delivery-open');
    setCheckExpanded(root, false);
    if (root._gfPanelHideTimer) clearTimeout(root._gfPanelHideTimer);
    root._gfPanelHideTimer = setTimeout(function () {
      if (!isPanelOpen(root)) panel.hidden = true;
      root._gfPanelHideTimer = null;
    }, PANEL_TRANSITION_MS);
  }

  function checkedZip(root, form) {
    var stored = trim(root.getAttribute('data-gf-checked-zip') || '');
    var input = form && form.querySelector('.gf-pdp-zip__input');
    var typed = input ? trim(input.value) : '';
    return stored || typed;
  }

  function estimateFromRoot(root, destination) {
    var product = productFromRoot(root);
    var variant = getSelectedVariant(root);
    var sku = variantSku(variant) || skuFromPage(root);
    if (variant && sku && !variant.sku) variant.sku = sku;
    return calculateEstimate({
      tags: product.tags,
      productType: product.type,
      editorCategory: product.editorCategory,
      vendor: product.vendor,
      fulfillmentService: product.fulfillmentService,
      sku: sku,
      variant: variant,
      weightUnit: product.weightUnit,
      freeShippingOffer: product.freeShippingOffer,
      destinationCountry: destination,
      now: new Date(),
    });
  }

  function applyEstimate(root, zipValue, options) {
    var settings = options || {};
    var country = destinationCountry(root);
    var parsed = normalizePostal(country, zipValue);
    if (!parsed.ok) {
      setNote(root, invalidPostalMessage(country), 'is-err');
      clearResult(root);
      root.removeAttribute('data-gf-checked-zip');
      closePanel(root);
      return Promise.resolve(false);
    }
    if (root.querySelector('.gf-pdp-zip__input')) {
      root.querySelector('.gf-pdp-zip__input').value = parsed.value;
    }

    var reuseLookup = settings.lookup && settings.lookup.valid ? settings.lookup : null;
    var request = reuseLookup
      ? Promise.resolve(reuseLookup)
      : lookupPostal(country, parsed.value);

    return request.then(function (lookup) {
      // Format already passed. A Zippopotam 404 / miss is not an invalid postal.
      // Show the estimate without inventing city/state.
      var resolved = lookup && lookup.valid ? lookup : { valid: false };
      rememberZip(root, parsed.value);
      writeStoredDestination(country, parsed.value);
      var result = estimateFromRoot(root, country);
      renderDeliveryResult(root, result, resolved);
      setNote(root, '', '');
      if (settings.open === false) {
        if (isPanelOpen(root)) openPanel(root);
      } else {
        openPanel(root);
      }
      return true;
    });
  }

  function writeVariantScript(root, variant) {
    var script = root.querySelector('[data-gf-pdp-variant]');
    if (!script || !variant) return;
    script.textContent = JSON.stringify({
      id: variant.id,
      sku: variant.sku,
      weight: variant.weight,
      weight_unit: variant.weight_unit || variant.weightUnit || root.getAttribute('data-gf-weight-unit'),
      weight_in_unit: variant.weight_in_unit,
      grams: variant.grams,
      requires_shipping: variant.requires_shipping,
    });
  }

  function onVariantChange(event) {
    var detail = event && event.data ? event.data : {};
    var incoming = detail.variant;
    document.querySelectorAll('[data-gf-pdp-delivery]').forEach(function (root) {
      if (detail.sectionId && root.closest('product-info')) {
        var info = root.closest('product-info');
        if (info && info.dataset.section && String(info.dataset.section) !== String(detail.sectionId)) {
          return;
        }
      }
      var merged = mergeVariant(
        getSelectedVariant(root),
        incoming,
        root.getAttribute('data-gf-weight-unit')
      );
      writeVariantScript(root, merged);
      var zip = checkedZip(root, root.querySelector('[data-gf-pdp-zip]'));
      var country = destinationCountry(root);
      if (!zip || !normalizePostal(country, zip).ok) return;
      if (!isPanelOpen(root)) return;
      renderDeliveryResult(root, estimateFromRoot(root, country));
    });
  }

  function applyPostalChrome(root) {
    var country = destinationCountry(root);
    var rule = postalRule(country);
    var input = root.querySelector('.gf-pdp-zip__input');
    var label = root.querySelector('[data-gf-pdp-zip-label]');
    var hint = root.querySelector('.gf-pdp-delivery-hint');
    if (label) label.textContent = rule.label;
    if (hint) hint.textContent = deliveryHintText(country);
    if (!input) return;
    input.setAttribute('type', 'text');
    input.placeholder = rule.placeholder;
    input.setAttribute('inputmode', postalInputMode(country));
    input.setAttribute('maxlength', String(rule.maxlength || 12));
  }

  function watchCountryChrome(root) {
    if (!root || typeof MutationObserver === 'undefined' || root._gfCountryObserver) return;
    root._gfCountryObserver = new MutationObserver(function () {
      applyPostalChrome(root);
    });
    root._gfCountryObserver.observe(root, { attributes: true, attributeFilter: ['data-gf-country'] });
  }

  function bindRoot(root) {
    if (!root || root.getAttribute('data-gf-delivery-init')) return;
    root.setAttribute('data-gf-delivery-init', 'true');
    var form = root.querySelector('[data-gf-pdp-zip]');
    if (!form) return;
    applyPostalChrome(root);
    watchCountryChrome(root);
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var input = form.querySelector('.gf-pdp-zip__input');
      applyEstimate(root, input ? input.value : '');
    });
    var zipInput = form.querySelector('.gf-pdp-zip__input');
    if (zipInput) {
      zipInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.keyCode === 13) {
          e.preventDefault();
          applyEstimate(root, zipInput.value);
        }
      });
      zipInput.addEventListener('input', function () {
        var country = destinationCountry(root);
        var parsed = normalizePostal(country, zipInput.value);
        var checked = trim(root.getAttribute('data-gf-checked-zip') || '');
        if (!trim(zipInput.value)) {
          clearStoredDestination();
        }
        if (parsed.ok && checked && checked === parsed.value) return;
        clearResult(root);
        root.removeAttribute('data-gf-checked-zip');
        closePanel(root);
        setNote(root, '', '');
      });
    }
    var closeBtn = root.querySelector('[data-gf-pdp-delivery-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        closePanel(root);
      });
    }
    var stored = readStoredDestination(destinationCountry(root));
    if (stored) {
      var input = form.querySelector('.gf-pdp-zip__input');
      if (input) input.value = stored.postal;
      applyEstimate(root, stored.postal);
    }
  }

  function bindAll(scope) {
    (scope || document).querySelectorAll('[data-gf-pdp-delivery]').forEach(bindRoot);
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
  } else if (typeof document !== 'undefined') {
    document.addEventListener('DOMContentLoaded', function () {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
      }
    });
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('shopify:section:load', function (e) {
      bindAll(e.target);
    });
  }

  return {
    CONFIG: CONFIG,
    normalizeZip: normalizeZip,
    normalizePostal: normalizePostal,
    getProductOrigin: getProductOrigin,
    isCjSupplier: isCjSupplier,
    getSelectedVariant: getSelectedVariant,
    getWeightInPounds: getWeightInPounds,
    addBusinessDays: addBusinessDays,
    formatDateRange: formatDateRange,
    getShippingRate: getShippingRate,
    getReturnStatus: getReturnStatus,
    isNonReturnableCategory: isNonReturnableCategory,
    calculateEstimate: calculateEstimate,
    formatPlace: formatPlace,
    formatDeliveryTitle: formatDeliveryTitle,
    formatZipLocation: formatZipLocation,
    zippopotamCandidates: zippopotamCandidates,
    zippopotamUrl: zippopotamUrl,
    placeFromZippopotam: placeFromZippopotam,
    lookupPostal: lookupPostal,
    applyEstimate: applyEstimate,
    postalInputMode: postalInputMode,
    applyPostalChrome: applyPostalChrome,
    renderDeliveryResult: renderDeliveryResult,
    bindAll: bindAll,
  };
});
